/* eslint-disable react/forbid-prop-types,react/no-unstable-nested-components,react/destructuring-assignment */
import React, { Fragment } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import SaveIcon from '@mui/icons-material/Save';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Alert from '@mui/material/Alert';
import { BuiltInCustom } from 'relevant-shared/rtImporter/defaultGranularDims';
import { fromHbDim } from 'relevant-shared/rtImporter/utils';
import DfpGranularityAdsState from 'relevant-shared/dfp/dfpGranularityAdsState';
import { toDimSet } from 'relevant-shared/dfp/dfpDims';
import { equalExceptDbId } from 'relevant-shared/misc/misc';
import { MAGIC_NUMERIC_STR } from 'relevant-shared/reportData/constants';
import classes from '../../api/classes';
import ExternalSystemEdit from '../ExternalSystemEdit';
import PopupSelector from '../PopupSelector';
import { ActionButton } from '../ActionButton/ActionButton';
import DataGrid from '../DataGrid';
import progressMsgChannel from '../../lib/progressMsgChannel';
import { FormOf, SimpleAlert } from '../Wrappers';
import Disabler from '../Disabler';
import ReactUtils from '../../lib/reactUtils';
import Switch from '../Switch';
import Select from '../Select';
import { ValueSelector, requireWhenEnabled, getBuiltIn } from './utils';
import Snippets from './snippets';
import Base from '../../layouts/Base';
import { ConfirmDialog } from '../ConfirmDialog';
import RecordTableTools from '../RecordTableTools';
import HelpIconButton from '../HelpIconButton';
import SystemData from '../../lib/systemData';
import { triggerToast } from '../Toast/misc';

const { Constants } = DfpGranularityAdsState;
const { BaseAdserver, DfpGranularityDimSetting, Reports } = classes;

const msgChannel = (settings) => ({ msgChannel: progressMsgChannel(settings) });
const keysOf = (dims) => _.uniq(_.filter(_.map(dims, 'customKeyName')));

const renderValueList = (values, {idMap}) => {
	const valIdToVal = _.invert(idMap.valToId);
	const keyIdToKey = _.invert(idMap.keyToId);

	const valueDesc = (name) => (
		<>
			{_.chunk(name.split('_'), 2).map(([keyId, valId], idx, arr) => {
				const key = fromHbDim(keyIdToKey[keyId]);
				const val = valIdToVal[valId];
				return (
					<span key={keyId}>
						<b>{key || `[UNKNOWN: ${keyId}]`}</b>={val || `[UNKNOWN: ${valId}]`}
						{idx < arr.length - 1 ? ',\u00A0' : ''}
					</span>
				);
			})}
		</>
	);
	return (
		<DataGrid
			disableColumnMenu
			disableSelectionOnClick
			disableColumnReorder
			hideFooter
			identifier={({ name }) => name}
			definitions={[
				{
					key: 'name',
					title: 'Value',
				},
				{
					key: 'name',
					title: 'Meaning',
					format: valueDesc,
					flex: 3,
				},
			]}
			data={values}
		/>
	);
};

const renderExistingDims = ({ adsState }) => {
	const arrOf = ({ isMine, active }) => [
		{ on: isMine && active, color: 'green', status: 'Active' },
		{ on: active, color: 'black', status: 'Other active' },
		{ on: isMine, color: 'lightgreen', status: 'Inactive' },
		{ on: true, color: 'gray', status: 'Other inactive' },
	];
	const propsOf = (dim) => _.find(arrOf(dim), 'on');
	const idxOf = (dim) => _.findIndex(arrOf(dim), 'on');
	return (
		<DataGrid
			disableColumnMenu
			disableSelectionOnClick
			disableColumnReorder
			hideFooter
			identifier={({ gamId, name }) => gamId || name}
			definitions={[
				{
					key: 'name',
					title: 'Name',
					format: (name, obj) => (
						<span style={{ color: propsOf(obj).color }}>
							{name}
						</span>
					),
				},
				{
					key: 'name',
					title: 'Status',
					format: (__, obj) => propsOf(obj).status,
				},
				{
					key: 'numActiveValues',
					title: 'Number of active values',
					sortable: true,
					format: (numActiveValues, { values, name }) => {
						const activeOwn = _.filter(values, 'active');
						return !activeOwn.length ? numActiveValues : (
							<PopupSelector
								size="lg"
								linkTitle={numActiveValues.toString()}
								title={`Active values for: ${name}`}
								content={() => renderValueList(activeOwn, adsState)}
							/>
						);
					},
				},
			]}
			data={_.orderBy(adsState.customDimensions, [idxOf, 'name'])}
		/>
	);
};

const usageFields = ({ adsState, dimSet }) => [
	[
		'Used custom dimensions',
		adsState.activeDims.length,
		Constants.DEFAULT_MAX_CUSTOM_DIMS,
	], [
		'Used custom dimension combinations',
		adsState.comboCount,
		Constants.DEFAULT_MAX_CUSTOM_COMBOS,
	],
	[
		'Max values of any granularity dimension',
		_.max(_.map(adsState.myCustomDims, 'numActiveValues')) || 0,
		Constants.DEFAULT_MAX_VALUES_PER_DIM,
	],
	[
		'Maximum characters for active value',
		_(adsState.myCustomDims)
			.filter('active')
			.map('values')
			.flatten()
			.filter('active')
			.map((v) => v.name.length)
			.max() || 0,
		Constants.DEFAULT_MAX_CHARS_PER_VAL,
	],
	[
		'Approximated max report rows per ad unit',
		dimSet.approxComboCountMax(),
		Constants.MAX_COMBOS_PER_REPORT_UNIT,
	],
].map(([title, num, maxNum]) => ({ title, num, maxNum, overflow: num > maxNum }));

const renderSummary = (settings) => (
	<table>
		<tbody>
			{usageFields(settings).map(({ title, num, maxNum, overflow }) => (
				<tr key={title} style={{ color: overflow ? 'red' : 'black' }}>
					<td style={{ paddingRight: 15 }}>
						<i>{title}: </i>
					</td>
					<td>
						{`${num} / ${maxNum}`}
					</td>
				</tr>
			))}
		</tbody>
	</table>
);

const renderOpStatus = ({ status, error }, retryFn) => {
	if (status !== 'error') {
		return undefined;
	}
	return (
		<SimpleAlert
			severity="error"
			content={(
				<>
					The following error occured during the last update of the custom dimensions in Google Ad Manager
					<div>
						<b>
							{error || 'Unknown error'}
						</b>
					</div>
				</>
			)}
			action={(
				<ActionButton
					label="Retry failed operation"
					variant="outlined"
					onClick={retryFn}
				/>
			)}
		/>
	);
};

const renderReportSettings = ({ dimSet }) => {
	const numVal = (n, isErr) => <span style={{ color: isErr ? 'red' : 'gray' }}>({n})</span>;
	return (
		<>
			<span style={{ textDecoration: 'underline', fontWeight: 'bold' }}>
				Reporting order:
			</span>
			<HelpIconButton boxSx={{ maxWidth: 700 }}>
				{Snippets.reportDocs}
			</HelpIconButton>
			<div style={{ fontSize: 'smaller' }}>
				{dimSet.map((arr, idx) => (
					<span key={arr.id}>
						[
						{arr.map((dim, dimIdx) => (
							<span key={dim.name}>
								<span style={{ color: dim.isAdsCustom ? 'green' : 'black' }}>
									{dim.desc}
								</span> {numVal(dim.approxReportValues())}
								{dimIdx < arr.length - 1 ? ',  ' : ''}
							</span>
						))}
						]
						&nbsp;
						<b>{numVal(
							arr.approxComboCount(),
							arr.approxComboCount() > Constants.MAX_COMBOS_PER_REPORT_UNIT,
						)}</b>
						{idx < dimSet.length - 1 && (<br />)}
					</span>
				))}
			</div>
		</>
	);
};

const renderAdsState = (settings) => (
	<Card style={{ height: '100%' }}>
		<CardContent>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Typography variant="h2">
						{settings.title}
					</Typography>
				</Grid>
				<Grid item xs={12}>
					{renderSummary(settings)}
				</Grid>
				<Grid item xs={12}>
					{renderReportSettings(settings)}
				</Grid>
				<Grid item xs={12}>
					{renderExistingDims(settings)}
				</Grid>
			</Grid>
		</CardContent>
	</Card>
);

const setDimDefaults = (granularityDims, customStats) => {
	let { dimSettings } = granularityDims;
	const exists = (name) => _.some(dimSettings, { name });
	const defaultDims = BuiltInCustom.filter((c) => !exists(c.hbDim)).map(({ hbDim, values }) => (
		new DfpGranularityDimSetting({ name: hbDim, values })
	));
	dimSettings.push(...defaultDims);
	const nonExistingCustom = _.keys(customStats).filter((name) => !exists(name)).map((name) => (
		new DfpGranularityDimSetting({ name })
	));
	dimSettings.push(...nonExistingCustom);
	dimSettings = _.sortBy(dimSettings, ({ name }) => name.toLowerCase());
	granularityDims.dimSettings = dimSettings;
	return granularityDims;
};

class DfpGranularityDims extends React.Component {
	constructor(props) {
		super(props);
		this.state = {};
		this.overflows = {};
	}

	createSettings(settings) {
		return {
			...settings,
			dimSet: toDimSet(settings.dimSettings),
		};
	}

	async loadServerState() {
		const { system } = this.props;
		const adsState = await system.getAdsGranularityStateClient()
			|| await system.getAdsGranularityStateClient({
				loadIfNotSet: true,
				...msgChannel({ initMsg: 'Loading adserver state...' }),
			});
		let endTs = new Date().getTime();
		endTs -= endTs % (300 * 1000); // let's use cached reports during 5 minutes
		const msBack = SystemData.genericData.dfpGranularDimCustomLookupMs || 300 * 1000;
		const { data } = await Reports.reportHb({
			startTs: endTs - msBack,
			endTs,
			sums: ['pageViews'],
			groupBy: ['customParameterKey', 'customParameterValue'],
			isCustomParamMetaReport: true,
		});
		const customStats = _(data)
			.mapKeys((__, key) => `_${key}`)
			.pickBy((d, key) => {
				delete d[MAGIC_NUMERIC_STR]; // skip numerical (only) custom parameters
				return !_.isEmpty(d) && !Constants.FORBIDDEN_GRANULAR_CUSTOM_DIMS.includes(fromHbDim(key));
			})
			.value();
		const granularityDims = setDimDefaults(
			_.cloneDeep((await BaseAdserver.get(system.id)).granularityDims),
			customStats,
		);
		this.setState({
			adsState: new DfpGranularityAdsState({ copyFrom: adsState }),
			granularityDims,
			orgDimSettings: _.cloneDeep(granularityDims.dimSettings),
			customStats,
		});
	}

	async reloadAdsState() {
		const { system } = this.props;
		const adsState = await system.reloadGranularityAdsStateClient(msgChannel({
			initMsg: 'Reloading adserver state...',
		}));
		this.setState({
			adsState: new DfpGranularityAdsState({ copyFrom: adsState }),
		});
	}

	async performUpdate(extraParams) {
		const { system } = this.props;
		const { granularityDims } = this.state;
		// Don't include non-changed default settings that we added previously
		const dimSettings = granularityDims.dimSettings.filter((dim) => (
			!equalExceptDbId(dim, new DfpGranularityDimSetting({
				name: dim.name,
				values: getBuiltIn(dim)?.values || [],
			}))
		));
		await system.updateDimSettingsClient({ dimSettings, ...msgChannel(), ...extraParams });
		await this.loadServerState();
	}

	hasChanges() {
		const { granularityDims, orgDimSettings } = this.state;
		const { dimSettings } = granularityDims;
		return !_.isEqual(dimSettings, orgDimSettings);
	}

	numActiveKeys() {
		const { adsState, granularityDims } = this.state;
		return adsState.otherCustomDims.length + keysOf(_.filter(granularityDims.dimSettings, 'enabled')).length;
	}

	checkOverflowOnRender(newSettings) {
		const { overflows } = this;
		let overflow = false;
		const trigger = (obj, settings) => setTimeout(() => triggerToast({
			message: `${obj.title} ${obj.num}/${obj.maxNum}`,
			timeout: 5,
			...settings,
		}));
		usageFields(newSettings).forEach((obj) => {
			if (obj.overflow) {
				overflow = true;
				if (!overflows[obj.title]) {
					overflows[obj.title] = true;
					trigger(obj, { status: 'error' });
				}
			} else if (overflows[obj.title]) {
				overflows[obj.title] = false;
				trigger(obj, { status: 'success' });
			}
		});
		return overflow;
	}

	keyItemsForDim(dim) {
		const { system } = this.props;
		const { adsState, granularityDims } = this.state;
		const { dimSettings } = granularityDims;
		const { myCustomDims } = adsState;
		const myCustomByName = _.keyBy(myCustomDims, 'name');

		// If the there is a custom dimension that is not used or only used by this dimension, then it's no use
		// to add even more dimensions. Therefore, 'hasForMe' implies we should not add a (new) option in dropdown.
		const hasForMe = myCustomDims.some(({ name }) => {
			const users = _.filter(dimSettings, { customKeyName: name });
			return !users.length || (users.length === 1 && users[0] === dim);
		}) || (dim.customKeyName && _.filter(dimSettings, { customKeyName: dim.customKeyName }).length === 1);

		const numActiveKeys = this.numActiveKeys();
		const canAdd = numActiveKeys < Constants.DEFAULT_MAX_CUSTOM_DIMS;
		const availKeys = _.map(myCustomDims.filter((d) => d.active || canAdd), 'name');
		availKeys.push(..._.difference(keysOf(dimSettings), availKeys));
		if (canAdd && !hasForMe) {
			const existing = _.keyBy(availKeys);
			for (let i = 1; ; ++i) {
				const name = `${Constants.DIM_NAME_START}${system.networkCode}_${i}`;
				if (!existing[name]) {
					availKeys.push(name);
					break;
				}
			}
		}
		const renderLabel = (name, suffix) => {
			const parts = name.split('_');
			return (
				<>
					<span style={{ color: 'gray', fontSize: 'small' }}>{_.initial(parts).join('_')}_</span>
					<b>{_.last(parts)}</b>
					&nbsp;{suffix}
				</>
			);
		};
		return availKeys.map((name) => {
			let suffix = '';
			const existing = myCustomByName[name];
			if (!existing) {
				suffix = ' (new)';
			} else if (!existing.active) {
				suffix = ' (activate)';
			}
			return { value: name, label: renderLabel(name, suffix) };
		});
	}

	renderDimSettings({ field, model, form }) {
		const { customStats } = this.state;
		const { dimSettings } = model;
		const dimsAndFields = _.sortBy(
			ReactUtils.fldMap(model, field, 'dimSettings', (dim, fld) => ({ dim, fld })),
			({ dim }) => fromHbDim(dim.name).toLowerCase(),
		);
		return (
			<Grid container spacing={3}>
				{dimsAndFields.map(({ dim, fld }) => (
					<Grid item xs={12} style={{ paddingTop: 4 }} key={dim.name}>
						<Card>
							<CardContent style={{ padding: '4px 16px' }}>
								<b>
									{fromHbDim(dim.name)}
								</b>
								<Grid container spacing={3}>
									<Grid item xs={2}>
										<Switch
											label="Enabled"
											{...fld('enabled')}
										/>
									</Grid>
									<Grid item xs={3}>
										<Select
											label="Custom Dimension Key"
											nonSelected="(none)"
											{...fld('customKeyName').validation(requireWhenEnabled(dim))}
											items={_.sortBy(this.keyItemsForDim(dim), 'value')}
											fullWidth
										/>
									</Grid>
									<Grid item xs={4}>
										<ValueSelector dim={dim} field={fld} customStats={customStats} />
									</Grid>
									<Grid item xs={2} />
									<Grid item xs={1}>
										<RecordTableTools
											deleteAction={() => (
												form.setVals({ dimSettings: _.without(dimSettings, dim) })
											)}
											title="Confirmation"
											confirmProps={{
												text: (
													<div>
														Are you sure you want to delete <b>{fromHbDim(dim.name)}</b>
													</div>
												),
											}}
										/>
									</Grid>
								</Grid>
							</CardContent>
						</Card>
					</Grid>
				))}
			</Grid>
		);
	}

	renderGranularitySettings(params) {
		const { popup, form } = params;
		const { adsState, granularityDims, orgDimSettings } = this.state;
		const { newState, changes } = adsState.withDimSettings({ ...granularityDims, modifyIdMap: true });
		const dimSettings = this.renderDimSettings(params);
		const reload = (fn) => popup.op.reload(fn);
		const update = (extra) => reload(() => this.performUpdate({ updateWithoutChanges: true, ...extra }));
		const oldSettings = this.createSettings({
			adsState,
			title: 'Current custom dimensions',
			dimSettings: orgDimSettings,
		});
		const newSettings = this.createSettings({
			adsState: newState,
			title: 'Custom dimensions after update',
			dimSettings: granularityDims.dimSettings,
		});
		const overflow = this.checkOverflowOnRender(newSettings);
		return (
			<Grid container spacing={3}>
				<div style={{ width: '100%' }}>
					{renderOpStatus(granularityDims, () => update({ dimSettings: undefined }))}
				</div>
				<Grid item xs={12}>
					<Grid container spacing={3}>
						<Grid item>
							<ActionButton
								label="Save and apply settings"
								disabled={(!this.hasChanges() && !changes)
									|| form.hasErrors(true)
									|| overflow}
								icon={<SaveIcon />}
								onClick={() => update()}
								variant="outlined"
							/>
						</Grid>
						<Grid item>
							<ActionButton
								label="Reload state from GAM"
								icon={<SaveIcon />}
								onClick={() => reload(() => this.reloadAdsState())}
								variant="outlined"
							/>
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={6}>
					{renderAdsState(oldSettings)}
				</Grid>
				<Grid item xs={6}>
					{renderAdsState(newSettings)}
				</Grid>
				<Grid item xs={12}>
					<Card>
						<CardContent>
							<Grid container spacing={3}>
								<Grid item xs={12}>
									<Typography variant="h2">
										Granular dimension settings
									</Typography>
								</Grid>
								<Grid item xs={12}>
									{dimSettings}
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		);
	}

	render() {
		const { system } = this.props;
		return (
			<>
				<Disabler disabled={system.isNew}>
					<PopupSelector
						title="Edit granular dimensions used for improved Adx reporting"
						okLabel="Close"
						autoExpandKey="granularDims"
						size="lg"
						fn={() => this.loadServerState()}
						onExpandChange2={(expanded, { popup }) => {
							if (expanded !== false || !this.hasChanges()) {
								return true;
							}
							Base.renderGlobal((closeFn) => (
								<ConfirmDialog
									open
									text="You have unsaved changes that will be lost. Are you sure you want to close this dialog?"
									onAny={closeFn}
									onConfirm={() => popup.forceClose()}
								/>
							));
							return false;
						}}
						content={(popup) => (
							<FormOf
								model={this.state.granularityDims}
								content={(p) => this.renderGranularitySettings({ ...p, popup })}
							/>
						)}
					/>
				</Disabler>
				{system.isNew && (
					<Alert severity="info">
						You have not saved this new adserver yet. In order to edit, please save first.
					</Alert>
				)}
			</>
		);
	}
}

DfpGranularityDims.propTypes = {
	system: PropTypes.object.isRequired,
};

DfpGranularityDims.defaultProps = {
};

ExternalSystemEdit.registerCustomRenderer('DfpGranularityDims', (p) => (<DfpGranularityDims {...p} />));
